import { Controller } from "@hotwired/stimulus";

// QCM player.
//
// State machine:
//   • blank   → user picks answers, click Valider → POST.
//   • success → answers locked, green chips on what they picked.
//   • failed  → user can see their own mistakes (red chips), bonnes réponses
//               jamais révélées. Cooldown timer before "Réessayer" unlocks.
//               Réessayer DELETEs the previous QcmAnswer and reloads the
//               surrounding Turbo Frame so the lesson re-renders blank.
//
// Wire-up:
//   POST /juniors/:id/qcm_answers              — submit a fresh attempt
//   DELETE /juniors/:id/qcm_answers/:answer_id — drop a failed attempt
export default class extends Controller {
  static values = {
    elementId:       String,
    progressId:      String,
    juniorId:        String,
    userId:          String,
    alreadyAnswered: Boolean,
    isFailed:        Boolean,
    answerId:        String,
    retryAt:         String
  };
  static targets = ["answer", "feedback", "submit", "retry", "retryLabel", "cooldownLabel"];

  connect() {
    if (this.isFailedValue) {
      this._startCooldown();
      return;
    }
    if (this.alreadyAnsweredValue) return;
    this.choices = {}; // { qIdx: Set<aIdx> }
  }

  disconnect() {
    if (this._cooldownTimer) clearInterval(this._cooldownTimer);
  }

  // ── Picking answers (blank state only) ─────────────────────────────
  toggleAnswer(event) {
    if (this.alreadyAnsweredValue) return;
    event.preventDefault();

    const label = event.currentTarget;
    const qIdx = label.dataset.questionIndex;
    const aIdx = parseInt(label.dataset.answerIndex, 10);
    if (qIdx == null || Number.isNaN(aIdx)) return;

    const set = this.choices[qIdx] || new Set();
    if (set.has(aIdx)) set.delete(aIdx); else set.add(aIdx);
    this.choices[qIdx] = set;

    const selected = set.has(aIdx);
    label.classList.toggle("is-selected", selected);
    const checkbox = label.querySelector('input[type="checkbox"]');
    if (checkbox) checkbox.checked = selected;
  }

  // ── Submit ─────────────────────────────────────────────────────────
  async submit() {
    if (this.alreadyAnsweredValue) return;

    const payload = {};
    Object.entries(this.choices).forEach(([k, v]) => { payload[k] = Array.from(v.values()); });

    if (this.hasSubmitTarget) this.submitTarget.disabled = true;
    if (this.hasFeedbackTarget) this.feedbackTarget.textContent = "Envoi…";

    try {
      const response = await fetch(`/juniors/${this.juniorIdValue}/qcm_answers`, {
        method: "POST",
        headers: this._headers(),
        credentials: "same-origin",
        body: JSON.stringify({
          qcm_answer: {
            user_id: this.userIdValue,
            lesson_element_id: this.elementIdValue,
            content: JSON.stringify(payload)
          }
        })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      this._reloadFrame();
      // Notify the timer so the lesson progress refreshes.
      this.element.dispatchEvent(new CustomEvent("lc-training-element:progress-changed", {
        bubbles: true,
        detail: { reason: "qcm-submitted", success: data.validation === true }
      }));
    } catch (_e) {
      if (this.hasFeedbackTarget) {
        this.feedbackTarget.classList.add("is-error");
        this.feedbackTarget.textContent = "Erreur réseau — réessaie.";
      }
      if (this.hasSubmitTarget) this.submitTarget.disabled = false;
    }
  }

  // ── Retry after cooldown ───────────────────────────────────────────
  async retry() {
    if (!this.answerIdValue) return;
    if (this.hasRetryTarget) this.retryTarget.disabled = true;
    if (this.hasRetryLabelTarget) this.retryLabelTarget.textContent = "Suppression…";

    try {
      const response = await fetch(`/juniors/${this.juniorIdValue}/qcm_answers/${this.answerIdValue}`, {
        method: "DELETE",
        headers: this._headers(),
        credentials: "same-origin"
      });
      if (response.status === 429) {
        const data = await response.json();
        this._setCooldown((data.remaining || 60) * 1000);
        if (this.hasRetryLabelTarget) this.retryLabelTarget.textContent = "Réessayer";
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this._reloadFrame();
    } catch (_e) {
      if (this.hasCooldownLabelTarget) {
        this.cooldownLabelTarget.textContent = "Erreur — réessaie dans un instant.";
      }
      if (this.hasRetryTarget) this.retryTarget.disabled = false;
      if (this.hasRetryLabelTarget) this.retryLabelTarget.textContent = "Réessayer";
    }
  }

  // ── Cooldown timer ────────────────────────────────────────────────
  _startCooldown() {
    if (!this.retryAtValue) return;
    const target = new Date(this.retryAtValue).getTime();
    if (Number.isNaN(target)) return;
    this._renderCooldown(target);
    this._cooldownTimer = setInterval(() => this._renderCooldown(target), 1000);
  }

  _setCooldown(ms) {
    const target = Date.now() + ms;
    this._renderCooldown(target);
    if (this._cooldownTimer) clearInterval(this._cooldownTimer);
    this._cooldownTimer = setInterval(() => this._renderCooldown(target), 1000);
  }

  _renderCooldown(target) {
    const remaining = Math.max(0, target - Date.now());
    if (remaining <= 0) {
      if (this.hasCooldownLabelTarget) {
        this.cooldownLabelTarget.textContent = "Tu peux retenter le quiz maintenant.";
      }
      if (this.hasRetryTarget) this.retryTarget.disabled = false;
      if (this._cooldownTimer) {
        clearInterval(this._cooldownTimer);
        this._cooldownTimer = null;
      }
      return;
    }
    const totalSeconds = Math.ceil(remaining / 1000);
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    const formatted = m > 0
      ? `${m}min ${String(s).padStart(2, "0")}s`
      : `${s}s`;
    if (this.hasCooldownLabelTarget) {
      this.cooldownLabelTarget.textContent = `Nouvelle tentative dans ${formatted}`;
    }
    if (this.hasRetryTarget) this.retryTarget.disabled = true;
  }

  // ── Helpers ────────────────────────────────────────────────────────
  _reloadFrame() {
    // Full page reload. We initially tried to repaint just the
    // <turbo-frame id="lc-training-content"> via `frame.src = ...` but the
    // refresh wasn't reliably triggered (the frame was created statically,
    // and Turbo doesn't always fire a fetch on assignment in that case).
    // The lesson URL embeds `?lesson_id=…`, so the user lands back on the
    // exact same lesson — they only lose scroll position, which is fine
    // since the QCM is at the bottom of the lesson.
    window.location.reload();
  }

  _headers() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
      Accept: "application/json"
    };
  }
}
